/**
 * Inbasket triage — natural-language finding (OE-126).
 *
 * Wire types shared by the `api-triageNlFind-default` callable and the Find
 * surface. The model NEVER selects particles: it only parameterises the
 * deterministic filter primitives (spec §5, locked decision 4), so the state
 * below mirrors the frontend's `TriageFilterState` exactly and renders as the
 * normal removable chips.
 *
 * Privacy (locked decision 4): the basket context carries METADATA ONLY —
 * sender names/addresses, basket size, and whether thread data exists. No
 * subjects, no bodies.
 */

export type TriageNlReadState = 'any' | 'unread' | 'read';
export type TriageNlOlderThan = null | '1w' | '1m' | '3m';

/** Mirror of the frontend `TriageFilterState` — keep in lock-step. */
export interface TriageNlFilterState {
    text: string;
    read: TriageNlReadState;
    /** Source buckets: gmail / outlook / checklist / other. Empty = all. */
    sources: string[];
    checklistDone: boolean;
    checklistMissed: boolean;
    hasAttachments: boolean;
    /** Only emitted when the basket actually carries thread ids (OE-125). */
    hasReply: boolean;
    olderThan: TriageNlOlderThan;
    fromEmails: string[];
}

export interface TriageNlSender {
    /** Display name when known (e.g. from `"Ina van Jaarsveld <ina@x>"`). */
    name?: string;
    address: string;
}

export interface TriageNlFindRequest {
    /** The user's sentence, e.g. "checklist items I missed from Jessica". */
    utterance: string;
    basketContext: {
        senders: TriageNlSender[];
        basketSize: number;
        /** Gates the hasReply primitive — see OE-125's hidden-until-data rule. */
        hasThreadIds: boolean;
    };
}

export interface TriageNlFindResponse {
    state: TriageNlFilterState;
    /** Optional server note, e.g. that hasReply was requested but unavailable. */
    note?: string;
}

/**
 * Server-side Find (OE-150): the triage counts/matches computed against the
 * search index instead of a whole-basket client load. The state shape is the
 * same filter-primitive mirror the NL endpoint uses.
 */
export interface TriageQueryRequest {
    state: TriageNlFilterState;
    /** Return the matching particleKeys (Select-all scope). Default false. */
    includeKeys?: boolean;
    /** Cap on returned keys; server clamps to its own maximum. */
    keysLimit?: number;
}

/**
 * Server-side bulk action (OE-151): File / Pend / Mark-read applied to a KEY
 * set on the server, so Select-all can act on matches the client never loaded.
 * Semantics mirror the frontend's own bulk paths exactly (bulk-actions.ts +
 * file-particle-payload.ts + the pend effect).
 */
export type TriageBulkActionType = 'file' | 'pend' | 'markRead';

export interface TriageBulkPendPayload {
    /**
     * The pend return instant as epoch millis, RESOLVED ON THE CLIENT — the
     * pend dialog's date/time are the user's local wall-clock, and only the
     * client knows that timezone (the server runs UTC).
     */
    performAtMillis: number;
    noteContent?: string;
}

export interface TriageBulkActionRequest {
    action: TriageBulkActionType;
    particleKeys: string[];
    /** Required when action = 'pend'. */
    pend?: TriageBulkPendPayload;
}

export interface TriageBulkActionResponse {
    /** Documents actually written. */
    applied: number;
    /** Keys not acted on: missing/foreign, system rows, already-read on markRead. */
    skipped: number;
}

export interface TriageQueryResponse {
    /** Same keys the Find panel renders: unread, read, checklistDone,
     *  checklistMissed, hasAttachments, hasReply, older:1w|1m|3m,
     *  source:gmail|outlook|checklist|other. */
    counts: Record<string, number>;
    /** Matches for the CURRENT state. */
    matchCount: number;
    /** The whole basket (the "of M" total) under the app-query scope. */
    basketSize: number;
    matchingKeys?: string[];
    /** True when matchingKeys hit the server cap (matchCount may exceed it). */
    keysCapped?: boolean;
}
